import React, { useEffect, useContext, useState } from 'react';
import { useMainLayoutContext } from '../../../providers/MainLayoutProvider';
import { Row, Col } from 'react-bootstrap';
import LazyLoadChart from '../../../components/common/LazyLoadChart';
import MachineTotalProduction from '../../../components/charts/machines-charts/MachineTotalProduction';
import getShiftStartEndTime, {
  updateTimeRange
} from '../../../components/charts/utils/chartUtils';
import dayjs from 'dayjs';
import { MachineDataContext } from '../../../providers/MachineDataContext';
import { useAppContext } from '../../../providers/AppProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import DisplayShift from '../../../components/base/DisplayShift';
import Badge from '../../../components/base/Badge';
import { UilStopwatch } from '@iconscout/react-unicons';
import MachineTotalMetersChart from '../../../components/charts/machines-charts/MachineTotalMetersChart';
import { WebSocketContext } from '../../../providers/WebSocketProvider';
import { FallingLines } from 'react-loader-spinner';
import MachinePercentageLineChart from '../../../components/charts/machines-charts/MachinePercentageLineChart';
import MachineSpeedGauge from '../../../components/charts/machines-charts/MachineSpeedGauge';
import ActiveBreakTimeBarChart from '../../../components/charts/machines-charts/ActiveBreakTimeBarChart';
import MachimeTime from '../../../components/cards/MachimeTime';
import MachineMaxActCounterChart from '../../../components/charts/machines-charts/MachineMaxActCounterChart';
import '../../../components/machines/trackingHeader.css';

const L70Traking = () => {
  const { setContentClass } = useMainLayoutContext();
  const { machineSingle, subscribeToChannel, unsubscribeToChannel } =
    useContext(WebSocketContext);
  const initialShiftTimes = getShiftStartEndTime();
  const [timeRange, setTimeRange] = useState({
    startTime: initialShiftTimes.startTime,
    endTime: initialShiftTimes.endTime
  });

  const { startTime, endTime } = timeRange;
  console.log(startTime, endTime);
  const currentDay = dayjs();
  const isWithinShift = currentDay.isBetween(dayjs(startTime), dayjs(endTime));

  const handleUpdate = direction => {
    const { startTime, endTime } = updateTimeRange(timeRange, direction);

    setTimeRange(prevRange => ({
      startTime,
      endTime
    }));
  };

  const {
    config: { theme }
  } = useAppContext();

  useEffect(() => {
    setContentClass('px-0 pt-navbar');
    return () => setContentClass('');
  }, []);
  useEffect(() => {
    console.log('Subscribing to machine-all channel');
    subscribeToChannel('machine-single', 'l70');

    return () => {
      console.log('UNSUBSCRIBING from machine-all channel');
      unsubscribeToChannel();
    };
  }, []);

  if (!machineSingle || machineSingle.machine !== 'l70') {
    return (
      <div style={styles.spinnerContainer}>
        <FallingLines
          color={theme === 'dark' ? 'white' : '#3874ff'}
          width="100"
          visible={true}
          ariaLabel="falling-circles-loading"
          // wrapperStyle ={styles.spinnerContainer}
        />
      </div>
    );
  }

  return (
    <Row className="g-0">
      <Col xs={12} xxl={12} className="px-0 bg-soft">
        <div className="px-4 px-lg-6 pt-6 pb-5">
          {/* Header Section */}
          {/* Same header row as the other seven tracking pages. It used to be
              flex-column below xl with align-items-start, which pinned the date
              navigator to the left edge; and a column flex-basis would fight the
              centring above. */}
          <div className="tracking-header mb-4">
            {/* Machine Title */}
            <h2 className="text-black fw-bolder m-0 tracking-header__title">Machine L70 Overview</h2>

            {/* Time Navigation */}
            <div className="d-flex align-items-center gap-3 ">
              <button
                className="btn btn-link p-0"
                aria-label="Previous date"
                onClick={() => handleUpdate(-1)}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#004DFF';
                  e.target.style.color = theme === 'dark' ? 'white' : 'black';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '';
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>

              <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {dayjs(startTime).format('MMMM D, YYYY, h:mm A')}
              </span>

              <button
                className="btn btn-link p-0"
                aria-label="Next date"
                onClick={() => handleUpdate(1)}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = '#004DFF';
                  e.target.style.color = theme === 'dark' ? 'white' : 'black';
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '';
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>

            {/* Shift Display */}
            {/* Right half — sizing lives in trackingHeader.css */}
            <div className="tracking-header__aside">
              <DisplayShift
                machineName={'l70'}
                bucket={'machine_data'}
                startTime={startTime}
                endTime={endTime}
              />
            </div>
          </div>

          {/* Status Badge */}
          {isWithinShift && (
            <div className="d-flex justify-content-start align-items-center gap-3 mb-4">
              <Badge
                variant="phoenix"
                bg={machineSingle.state.status.type}
                iconPosition="end"
                icon={<UilStopwatch size={12} className="ms-1" />}
              >
                {machineSingle.state.status.label}
              </Badge>
            </div>
          )}

          {/* Chart Row */}
          <Row className="gx-5 gy-4">
            {/* LEFT: Total Produced Title Chart (e.g. actual production) */}
            <Col xs={12} lg={6} xl={6}>
              <div className="h-100 d-flex flex-column justify-content-between">
                <LazyLoadChart>
                  <MachineTotalProduction
                    bucket={'machine_data'}
                    machineName={'l70'}
                    startTime={startTime}
                    endTime={endTime}
                    title={'Total Meter Produced On Time'}
                  />
                </LazyLoadChart>
              </div>
            </Col>

            {/* RIGHT: Total Meter Counter Chart (aggregated) */}
            <Col xs={12} lg={6} xl={6}>
              <div className="h-100 d-flex flex-column justify-content-between">
                <LazyLoadChart>
                  <MachineTotalMetersChart
                    dynamicBucket={'true'}
                    bucket={'machine_data_minute'}
                    machineName={'l70'}
                    fields={'total_produced'}
                    startTime={startTime}
                    endTime={endTime}
                  />
                </LazyLoadChart>
              </div>
            </Col>
            <Col xs={12} sm={4} xl={3} xxl={4} className="pe-xl-0">
              {isWithinShift && (
                <MachineSpeedGauge
                  name={'Line Speed'}
                  speed={machineSingle.state.currentPlcState.LINE_SPEED.toFixed(
                    2
                  )}
                  startTime={startTime}
                  endTime={endTime}
                />
              )}
            </Col>
            <Col xs={12} sm={8} xl={9} xxl={isWithinShift ? 8 : 12}>
              <LazyLoadChart>
                <MachinePercentageLineChart
                  dynamicBucket={'true'}
                  bucket={'machine_data_minute'}
                  title={'Line Speed'}
                  machineName={'l70'}
                  fields={'LINE_SPEED'}
                  startTime={startTime}
                  endTime={endTime}
                  machineStatus={'running'}
                />
              </LazyLoadChart>
            </Col>
            <Col xs={12} xl={12} xxl={12} className="pe-xl-0">
              {/* Number of Coils produced */}

              <LazyLoadChart>
                <ActiveBreakTimeBarChart
                  dynamicBucket={'true'}
                  bucket={'machine_data_30minute'}
                  machineName={'l70'}
                  fields={'ACTIVE_TIME,BREAK_TIME'}
                  startTime={startTime}
                  endTime={endTime}
                  title={'Active Break Time'}
                />
              </LazyLoadChart>
            </Col>
            <Col xs={12} sm={6} xl={6} xxl={6} className="pe-xl-0">
              {/* Number of Coils produced */}
              <LazyLoadChart>
                <MachimeTime
                  time={'totalActiveTime'}
                  bucket={'machine_data'}
                  machineName={'l70'}
                  startTime={startTime}
                  endTime={endTime}
                  title={'Active Machine Minutes'}
                />
              </LazyLoadChart>
            </Col>
            <Col xs={12} sm={6} xl={6} xxl={6} className="pe-xl-0">
              {/* Number of Coils produced */}
              <LazyLoadChart>
                <MachimeTime
                  time={'totalBreakTime'}
                  bucket={'machine_data'}
                  machineName={'l70'}
                  startTime={startTime}
                  endTime={endTime}
                  title={'Break Machine Minutes'}
                />
              </LazyLoadChart>
            </Col>
            <Col xl={12} xxl={12} className="pe-xl-0">
              <LazyLoadChart>
                <MachineMaxActCounterChart
                  bucket={'machine_data_minute'}
                  machineName={'l70'}
                  fields={'METER_ACT'}
                  startTime={startTime}
                  endTime={endTime}
                />
              </LazyLoadChart>
            </Col>
          </Row>
        </div>
      </Col>
    </Row>
  );
};

const styles = {
  spinnerContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    // backgroundColor: 'rgba(0, 0, 0, 0.5)', // Optional: Add a background with some opacity
    zIndex: 1000 // Ensure the spinner is on top
    // backgroundColor: '#000', // Optional: background color
  }
};

export default L70Traking;
